import React from 'react';
import { Avatar, Box, Tooltip } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

interface CompetitionImageProps {
  competitionId: string;
  hasImage: boolean;
  imageType?: 'logo' | 'icon';
  size?: 'small' | 'medium' | 'large';
  useFullSize?: boolean;
  tooltip?: string;
  withBorder?: boolean;
}

/**
 * A reusable component for displaying competition logos and icons
 */
const CompetitionImage: React.FC<CompetitionImageProps> = ({ 
  competitionId, 
  hasImage, 
  imageType = 'logo',
  size = 'medium',
  useFullSize = false,
  tooltip,
  withBorder = false
}) => {
  // Size mappings
  const dimensions = {
    small: { width: 32, height: 32 },
    medium: { width: 48, height: 48 },
    large: { width: 64, height: 64 }
  }[size];
  
  const iconSize = {
    small: 'small',
    medium: 'medium',
    large: 'large'
  }[size] as 'small' | 'medium' | 'large';
  
  if (!hasImage) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...dimensions }}>
        <EmojiEventsIcon color="primary" fontSize={iconSize} />
      </Box>
    );
  }
  
  const srcApi = useFullSize 
  ? `${baseURL}/api/images/Competition/${competitionId}/${imageType}`
  : `${baseURL}/api/images/Competition/${competitionId}/thumbnail/${imageType}`;

  const avatar = (
    <Avatar 
      src={srcApi}
      alt={imageType === 'logo' ? "Competition Logo" : "Competition Icon"}
      sx={{ 
        ...dimensions,
        bgcolor: 'primary.light',
        ...(withBorder && { border: '1px solid rgba(0,0,0,0.1)' })
      }}
      variant="rounded"
    >
      <EmojiEventsIcon fontSize={iconSize} />
    </Avatar>
  );
  
  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        {avatar}
      </Tooltip>
    );
  }
  
  return avatar;
};

export default CompetitionImage;